import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SpecialTag } from '../SpecialTag';
import { TagActionsMenu } from '../SpecialTag/TagActionsMenu';
import { variant } from '../SpecialTag/types';
import { orderStateVariant } from '../SpecialTag/orderStateVariant';
import type { LinkedWorkRef } from '../../../types/surveillanceRoute';

/** Píldora del trabajo + etiqueta de su estado, siempre en la misma fila. */
const WorkWithState = styled.span`
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
`;

interface LinkedWorkTagsProps {
  /** Trabajos (actuación o accidente) que atendieron el aviso. */
  works?: LinkedWorkRef[];
  /** Abrir el trabajo. Sin él (o con `disabled`) la pill es estática. */
  onOpen?: (work: LinkedWorkRef) => void;
  disabled?: boolean;
  sizeTag?: 'small' | 'medium' | 'default';
  testId?: string;
  /**
   * Texto de la pill, con `{{roid}}`. 30-oct: la tabla de Tramos la pinta en la
   * columna de la actuación y necesita distinguirla de una básica («De la OT ·
   * X»); la card, bajo «Vinculado con…», se queda con «Resuelto por X».
   */
  labelKey?: string;
  /**
   * Junto a cada píldora, la etiqueta del estado del trabajo (misma fila).
   * Solo la pide la tabla de Tramos; el resto de usos no cambia.
   */
  showState?: boolean;
}

/**
 * Pills «Resuelto por X» de los trabajos que atendieron un aviso a sala. Pieza
 * compartida por la tabla de Tramos y la card de «Órdenes asociadas». Sin hooks
 * de router ni de Redux: la tabla se pinta también en modales fuera del router,
 * la navegación llega por `onOpen`.
 */
export const LinkedWorkTags = ({
  works,
  onOpen,
  disabled,
  sizeTag = 'default',
  testId,
  labelKey = 'pages:surveillance.resolvedBy',
  showState = false,
}: LinkedWorkTagsProps): JSX.Element | null => {
  const { t } = useTranslation(['pages', 'common']);
  if (!works || works.length === 0) return null;
  const tagTestId = testId ?? 'segment-row-aviso-work';

  return (
    <>
      {works.map((work) => {
        const title = t(labelKey, { roid: work.roid });
        const stateVariant = showState ? orderStateVariant(work.state) : null;
        const stateTag = stateVariant ? (
          <SpecialTag
            variant={stateVariant}
            sizeTag="medium"
            data-testid="segment-row-work-state"
          />
        ) : null;
        const withState = (pill: JSX.Element): JSX.Element =>
          stateTag ? (
            <WorkWithState key={work.id}>
              {pill}
              {stateTag}
            </WorkWithState>
          ) : (
            pill
          );
        // Un solo toque abre: `TagActionsMenu` con una acción es la pill entera.
        if (onOpen && !disabled) {
          return withState(
            <TagActionsMenu
              key={work.id}
              variant={variant.LinkedFromSegment}
              sizeTag={sizeTag}
              title={title}
              testId={tagTestId}
              actions={[
                {
                  icon: 'view',
                  label: t('common:buttons.view'),
                  onClick: () => onOpen(work),
                },
              ]}
            />
          );
        }
        return withState(
          <SpecialTag
            key={work.id}
            variant={variant.LinkedFromSegment}
            sizeTag={sizeTag}
            title={title}
            data-testid={tagTestId}
          />
        );
      })}
    </>
  );
};

export default LinkedWorkTags;
